import React, { memo, useEffect } from 'react';

import Loading from '@/components/common/Loading';
import { TaskState } from '@/components/DrawerTaskPanel/useTaskList';
import { useGlobalAudioPlayer } from '@/contexts/GlobalAudioPlayerContext';
import { useTranslate } from '@/hooks/useTranslate';
import { getMediaDuration } from '@/utils/fileUtil';

import Waveform from '../../assets/_components/Waveform';
import styles from './index.module.scss';

export interface AudioPlayerProps {
  fileUrl: string;
  duration: number; // 秒
  state?: TaskState;
  className?: string;
}

const formatTime = (s: number) => {
  const totalSeconds = Math.floor(s);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// 统一状态转换

export const AudioPlayer: React.FC<AudioPlayerProps> = memo(
  ({ fileUrl, state, className = '' }) => {
    const { t } = useTranslate();
    const {
      isCurrentPlaying,
      play,
      pause,
      currentTime: globalCurrentTime,
    } = useGlobalAudioPlayer();
    const [duration, setDuration] = React.useState<number>(0);

    const isPlaying = isCurrentPlaying(fileUrl);
    const currentTime = isPlaying ? globalCurrentTime : 0;

    const handlePlayPause = () => {
      if (state !== TaskState.SUCCESS) return;

      if (isPlaying) {
        pause();
      } else {
        play(fileUrl);
      }
    };

    useEffect(() => {
      (async () => {
        if (!fileUrl) return;
        const time = await getMediaDuration(fileUrl);
        setDuration(time);
      })();
    }, [fileUrl]);

    const isLoading = state === TaskState.PENDING;
    const isSuccess = state === TaskState.SUCCESS;
    const isFailed = state === TaskState.FAILED;
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
      <div
        className={`${styles.audioPlayer} ${isLoading ? styles.loading : ''} ${state !== TaskState.SUCCESS ? styles.disabled : ''} ${className}`}
      >
        {isLoading ? (
          <div className={styles.loadingIcon}>
            <div className={styles.breathingIcon}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="21"
                viewBox="0 0 17 21"
                fill="none"
              >
                <path
                  d="M8.19958 1.83333C7.47024 1.83333 6.77077 2.12306 6.25504 2.63879C5.73932 3.15451 5.44958 3.85399 5.44958 4.58333V8.25C5.44958 8.97935 5.73932 9.67882 6.25504 10.1945C6.77077 10.7103 7.47024 11 8.19958 11C8.92893 11 9.6284 10.7103 10.1441 10.1945C10.6599 9.67882 10.9496 8.97935 10.9496 8.25V4.58333C10.9496 3.85399 10.6599 3.15451 10.1441 2.63879C9.6284 2.12306 8.92893 1.83333 8.19958 1.83333ZM8.19958 0C9.41516 0 10.5809 0.482885 11.4405 1.34243C12.3 2.20197 12.7829 3.36776 12.7829 4.58333V8.25C12.7829 8.85189 12.6644 9.44789 12.434 10.004C12.2037 10.56 11.8661 11.0653 11.4405 11.4909C11.0149 11.9165 10.5096 12.2541 9.95355 12.4844C9.39747 12.7148 8.80148 12.8333 8.19958 12.8333C7.59769 12.8333 7.00169 12.7148 6.44562 12.4844C5.88954 12.2541 5.38428 11.9165 4.95868 11.4909C4.53308 11.0653 4.19547 10.56 3.96514 10.004C3.7348 9.44789 3.61625 8.85189 3.61625 8.25V4.58333C3.61625 3.36776 4.09914 2.20197 4.95868 1.34243C5.81822 0.482885 6.98401 0 8.19958 0ZM0 9.16667H1.84708C2.06918 10.6927 2.8333 12.0878 3.99963 13.0967C5.16596 14.1055 6.65654 14.6608 8.19867 14.6608C9.74079 14.6608 11.2314 14.1055 12.3977 13.0967C13.564 12.0878 14.3281 10.6927 14.5503 9.16667H16.3983C16.1898 11.0263 15.3557 12.76 14.0325 14.0832C12.7094 15.4065 10.9759 16.2409 9.11625 16.4496V20.1667H7.28292V16.4496C5.42313 16.2411 3.68939 15.4068 2.36608 14.0835C1.04277 12.7602 0.208462 11.0265 0 9.16667Z"
                  fill="#4931FA"
                />
              </svg>
            </div>
          </div>
        ) : isSuccess ? (
          <button
            className={styles.playButton}
            onClick={handlePlayPause}
            disabled={isFailed}
          >
            {isPlaying ? PlayingIcon : PauseIcon}
          </button>
        ) : (
          FailedIcon
        )}

        {!isLoading && !isFailed && (
          <Waveform progress={progress} isPlaying={isPlaying} />
        )}

        <div className={styles.timeDisplay}>
          {isLoading ? (
            <span className="text-[#4931FA]">{t('merror.translation.voiceload')}...</span>
          ) : isFailed ? (
            <span className="text-[#FC5132FF]">{t('merror.tip.generationFailed')}</span>
          ) : (
            <>
              <span className={styles.currentTime}>{formatTime(currentTime)}</span>
              <span className={styles.separator}>/</span>
              <span className={styles.totalTime}>{formatTime(duration)}</span>
            </>
          )}
        </div>
      </div>
    );
  },
);

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;

const PlayingIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M3.33325 4.33301C3.33325 3.78072 3.78097 3.33301 4.33325 3.33301H11.6666C12.2189 3.33301 12.6666 3.78072 12.6666 4.33301V11.6663C12.6666 12.2186 12.2189 12.6663 11.6666 12.6663H4.33325C3.78097 12.6663 3.33325 12.2186 3.33325 11.6663V4.33301Z"
      fill="white"
    />
  </svg>
);

const PauseIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M12.3271 9.16598C13.2201 8.64504 13.2201 7.35471 12.3271 6.83377L5.55635 2.88417C4.65636 2.35918 3.52612 3.00836 3.52612 4.05028V11.9495C3.52612 12.9914 4.65636 13.6406 5.55635 13.1156L12.3271 9.16598Z"
      fill="white"
    />
  </svg>
);

const FailedIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M9.52734 6.07663V9.96552M9.52734 18.2778C4.69485 18.2778 0.777344 14.3603 0.777344 9.52783C0.777344 4.69534 4.69485 0.777832 9.52734 0.777832C14.3598 0.777832 18.2773 4.69534 18.2773 9.52783C18.2773 14.3603 14.3598 18.2778 9.52734 18.2778ZM9.57576 12.8822V12.9794L9.47892 12.979V12.8822H9.57576Z"
      stroke="#FC5132"
      strokeWidth="1.55556"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
